import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import NotificationsBell from "@/components/NotificationsBell";
import BrimiLogo from "@/components/BrimiLogo";
import {
  LayoutDashboard, User2, ClipboardList, Map, Compass, GraduationCap, MessageSquare,
  LogOut, Palette, CalendarDays, GitCompare, Users, Gamepad2, Share2,
} from "lucide-react";

const items = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/onboarding", label: "My Profile", icon: User2, testid: "nav-profile" },
  { to: "/quiz", label: "Interest Quiz", icon: ClipboardList, testid: "nav-quiz" },
  { to: "/roadmap", label: "Roadmap", icon: Map, testid: "nav-roadmap" },
  { to: "/careers", label: "Careers", icon: Compass, testid: "nav-careers" },
  { to: "/compare", label: "Compare", icon: GitCompare, testid: "nav-compare" },
  { to: "/simulator", label: "Career Simulator", icon: Gamepad2, testid: "nav-simulator" },
  { to: "/creative-studio", label: "Creative Studio", icon: Palette, testid: "nav-creative" },
  { to: "/colleges", label: "Colleges", icon: GraduationCap, testid: "nav-colleges" },
  { to: "/calendar", label: "Exams & Scholarships", icon: CalendarDays, testid: "nav-calendar" },
  { to: "/chat", label: "AI Coach", icon: MessageSquare, testid: "nav-chat" },
  { to: "/career-card", label: "Career Card", icon: Share2, testid: "nav-career-card" },
  { to: "/share", label: "Parent Portal", icon: Users, testid: "nav-parent-share" },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  return (
    <aside className="hidden md:flex flex-col w-64 min-h-screen bg-[#0F1B33] border-r border-white/5 sticky top-0" data-testid="app-sidebar">
      <div className="px-6 py-6 border-b border-white/5 flex items-center justify-between">
        <BrimiLogo size={36} withBadge showText textColor="white" />
        <NotificationsBell />
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {items.map(({ to, label, icon: Icon, testid }) => (
          <NavLink
            key={to} to={to} data-testid={testid}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                isActive
                  ? "bg-white text-slate-900"
                  : "text-slate-300 hover:bg-white/5 hover:text-white"
              }`
            }
          >
            <Icon className="w-4 h-4" />
            {label}
          </NavLink>
        ))}
      </nav>

      <div className="p-4 border-t border-white/5">
        <div className="flex items-center gap-3 mb-3">
          {user?.picture ? (
            <img src={user.picture} alt={user.name} className="w-9 h-9 rounded-full object-cover" />
          ) : (
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-yellow-400 to-coral-500 flex items-center justify-center text-slate-900 font-bold" style={{ background: "linear-gradient(135deg, #FBBF24, #FB7185)" }}>
              {user?.name?.[0]?.toUpperCase() || "S"}
            </div>
          )}
          <div className="min-w-0">
            <p className="text-sm font-semibold text-white truncate" data-testid="sidebar-user-name">{user?.name || "Student"}</p>
            <p className="text-xs text-slate-400 truncate">{user?.email}</p>
          </div>
        </div>
        <button
          data-testid="logout-button"
          onClick={async () => { await logout(); nav("/"); }}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-full text-sm font-bold bg-white/5 text-slate-200 hover:bg-white/10 transition"
        >
          <LogOut className="w-4 h-4" /> Log out
        </button>
      </div>
    </aside>
  );
}
